import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, get, post, setWorkspaceSlug } from './api';
import { isMobileAppRuntime } from './mobile-runtime';
import type { BootstrapData } from './types';

interface AppContextValue {
  bootstrap: BootstrapData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  login: (email: string, password: string, workspaceChoice?: string) => Promise<void>;
  logout: () => Promise<void>;
  markNotificationRead: (id: number) => Promise<void>;
  markAllNotificationsRead: () => Promise<void>;
  clearNotifications: () => Promise<void>;
  acknowledgeNotification: (id: number) => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [bootstrap, setBootstrap] = useState<BootstrapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await get<BootstrapData>('/api/bootstrap');
      setBootstrap(data);
      if (data.workspace?.slug) setWorkspaceSlug(data.workspace.slug);
      setError(null);
    } catch (requestError) {
      const status = (requestError as { status?: number }).status;
      if (status === 401) { setBootstrap(null); setError(null); }
      else setError((requestError as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (!isMobileAppRuntime()) return;
    const refreshWhenActive = () => { if (document.visibilityState === 'visible') void refresh(); };
    const interval = window.setInterval(refreshWhenActive, 30_000);
    window.addEventListener('focus', refreshWhenActive);
    window.addEventListener('spacedesk:notification', refreshWhenActive as EventListener);
    document.addEventListener('visibilitychange', refreshWhenActive);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshWhenActive);
      window.removeEventListener('spacedesk:notification', refreshWhenActive as EventListener);
      document.removeEventListener('visibilitychange', refreshWhenActive);
    };
  }, [refresh]);

  const login = useCallback(async (email: string, password: string, workspaceChoice?: string) => {
    // Ignore a previous user's remembered workspace. The authenticated response
    // supplies the tenant before bootstrap loads any operational data.
    setWorkspaceSlug('');
    const signedIn = await post<{ workspace?: { slug: string } | null }>('/api/auth/login', {
      email, password, autoWorkspace: true, ...(workspaceChoice ? { workspaceChoice } : {}),
    });
    setWorkspaceSlug(signedIn.workspace?.slug || '');
    setLoading(true);
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST' });
    setBootstrap(null);
  }, []);

  const markNotificationRead = useCallback(async (id: number) => {
    await post(`/api/notifications/${id}/read`);
    setBootstrap((current) => current ? {
      ...current,
      notifications: current.notifications.map((item) => item.id === id ? { ...item, is_read: 1 } : item),
      unreadNotifications: Math.max(0, current.unreadNotifications - (current.notifications.find((item) => item.id === id)?.is_read ? 0 : 1)),
    } : current);
  }, []);

  const markAllNotificationsRead = useCallback(async () => {
    await post('/api/notifications/read-all');
    setBootstrap((current) => current ? {
      ...current,
      notifications: current.notifications.map((item) => ({ ...item, is_read: 1 })),
      unreadNotifications: 0,
    } : current);
  }, []);

  const clearNotifications = useCallback(async () => {
    await post('/api/notifications/clear');
    await refresh();
  }, [refresh]);

  const acknowledgeNotification = useCallback(async (id: number) => {
    await post(`/api/notifications/${id}/acknowledge`);
    setBootstrap((current) => current ? {
      ...current,
      notifications: current.notifications.map((item) => item.id === id ? { ...item, is_read: 1 } : item),
      unreadNotifications: Math.max(0, current.unreadNotifications - (current.notifications.find((item) => item.id === id)?.is_read ? 0 : 1)),
    } : current);
  }, []);

  const value = useMemo(() => ({ bootstrap, loading, error, refresh, login, logout, markNotificationRead, markAllNotificationsRead, clearNotifications, acknowledgeNotification }),
    [bootstrap, loading, error, refresh, login, logout, markNotificationRead, markAllNotificationsRead, clearNotifications, acknowledgeNotification]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used inside AppProvider.');
  return context;
}
